import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Button,
  Divider,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";
import LinkIcon from "@material-ui/icons/Link";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import { Column } from "devextreme-react/data-grid";
import { TextBox, Button as TextBoxButton } from "devextreme-react/text-box";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import TagBox from "../common/CustomTagBox";
import DateBox from "../common/CustomDateBox";
import { baseRequest } from "../../fetch/basicThunk";
import Table from "../table/Table";
import CustomPagination from "../table/CustomPagination";
import LinkParentTicketModal from "./LinkParentTicketModal";
import { openModal } from "../../redux/modal/modalSlice";
import { _base64ToArrayBuffer } from "../../commonFunctions/commonFunctions";
import { renderCaseStateColumn } from "../../Constantes/constantesColumnasTablas";
import { getSuppliers } from "../../redux/suppliers/thunks";
import { getSupplierServiceTypes } from "../../redux/supplierServiceTypes/thunks";
import { getCaseStates } from "../../redux/caseStates/thunks";
import { getAllCustomers } from "../../redux/customers/thunks";
import { selectSuppliers } from "../../redux/suppliers/selectors";
import { selectSupplierServiceTypes } from "../../redux/supplierServiceTypes/selectors";
import { selectCaseStates } from "../../redux/caseStates/selectors";
import { selectAllCustomers } from "../../redux/customers/selectors";
import searchIcon from "../../images/ui-ic-search.svg";
import exportIcon from "../../images/export.svg";
import importIcon from "../../images/importIcon.svg";
import checkmarkIcon from "../../images/checkmark.svg";
import alertWarningIcon from "../../images/alert-warning.svg";
import alertInfoIcon from "../../images/alert-info.svg";
import {
  QUERY_GET_BILLING_TICKETS,
  QUERY_GET_BILLING_DRAFTS,
  QUERY_POST_BILLING_IMPORT,
  QUERY_POST_BILLING_ASSOCIATE_DRAFT,
  QUERY_POST_BILLING_EXPORT,
  QUERY_POST_BILLING_UNLINK_PARENT,
} from "../../fetch/constantQueries";

const useStyles = makeStyles({
  display: {
    display: "inline-block !important",
  },
});

export function ChildTicketsSubTable({
  childTickets,
  renderFinancialStatus,
  renderCaseStateColumn,
}) {
  const containerRef = useRef(null);
  const [colWidths, setColWidths] = useState([]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const gridElem = containerRef.current.closest(".dx-datagrid");
      if (gridElem) {
        const headerCols = gridElem.querySelectorAll(
          ".dx-datagrid-headers .dx-datagrid-table colgroup col"
        );
        if (headerCols.length) {
          const widths = Array.from(headerCols).map(
            (c) => c.style.width || `${c.getBoundingClientRect().width}px`
          );
          setColWidths(widths);
        }
      }
    }
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ margin: 0, padding: 0, borderRadius: 0, overflow: "hidden" }}
    >
      <table
        className="dx-datagrid-table dx-datagrid-table-fixed"
        style={{
          width: "100%",
          borderCollapse: "collapse",
          margin: 0,
          padding: 0,
          borderRadius: 0,
          tableLayout: "fixed",
        }}
      >
        <colgroup>
          {colWidths.length > 0 ? (
            colWidths.map((w, i) => <col key={i} style={{ width: w }} />)
          ) : (
            <>
              <col style={{ width: "36px" }} />
              <col />
              <col style={{ width: "10%" }} />
              <col style={{ width: "6%" }} />
              <col />
              <col style={{ width: "6%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col />
              <col />
            </>
          )}
        </colgroup>
        <tbody>
          {childTickets.map((child, idx) => (
            <tr
              key={child.caseId || idx}
              className="dx-row dx-data-row"
              style={{
                borderLeft: "4px solid #9E2896",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#fff",
                cursor: "pointer",
                height: "38px",
              }}
              onClick={() => {
                if (child.caseId) {
                  window.location.href = `/?cod_cliente=${child.caseId}`;
                }
              }}
            >
              <td style={{ padding: "7px 8px", textAlign: "center" }}></td>
              <td style={{ padding: "7px 8px", textAlign: "right" }}>
                {child.caseNumber || child.caseId}
              </td>
              <td
                style={{
                  padding: "7px 8px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {child.customerName || ""}
              </td>
              <td style={{ padding: "7px 8px" }}>{child.mspid || ""}</td>
              <td
                style={{
                  padding: "7px 8px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {child.supplierName || ""}
              </td>
              <td style={{ padding: "7px 8px" }}>
                {child.vehiclePlate || ""}
              </td>
              <td style={{ padding: "7px 8px" }}>
                {child.supplierProtocol || ""}
              </td>
              <td style={{ padding: "7px 8px" }}>
                {renderFinancialStatus(child.financialStatus)}
              </td>
              <td style={{ padding: "7px 8px", textAlign: "right" }}>
                {child.financialStatus === "UNLINKED"
                  ? "-"
                  : `${child.paymentOrdersCount || 0} OP${
                      child.paymentOrdersCount === 1 ? "" : "s"
                    }`}
              </td>
              <td style={{ padding: "7px 8px", textAlign: "right" }}>
                {child.totalValue != null
                  ? `R$ ${Number(child.totalValue).toFixed(2)}`
                  : "-"}
              </td>
              <td style={{ padding: "7px 8px", textAlign: "right" }}>
                {child.approvedTotalValue != null
                  ? `R$ ${Number(child.approvedTotalValue).toFixed(2)}`
                  : "-"}
              </td>
              <td style={{ padding: "7px 8px" }}>
                {child.startDate
                  ? dayjs(child.startDate).format("DD/MM/YYYY")
                  : ""}
              </td>
              <td style={{ padding: "7px 8px" }}>
                {child.endDate ? dayjs(child.endDate).format("DD/MM/YYYY") : ""}
              </td>
              <td
                style={{
                  padding: "7px 8px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {child.agent || ""}
              </td>
              <td style={{ padding: "7px 8px" }}>
                {renderCaseStateColumn(child.caseStateId, child.caseState)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BillingManagement() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState("idle");
  const [ticketPage, setTicketPage] = useState({
    pageIndex: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 0,
  });
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [selectedChildTicket, setSelectedChildTicket] = useState(null);

  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState("idle");
  const [draftPage, setDraftPage] = useState({
    pageIndex: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 0,
  });

  const [filterText, setFilterText] = useState("");
  const [filterSuppliers, setFilterSuppliers] = useState([]);
  const [filterServiceTypes, setFilterServiceTypes] = useState([]);
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [filterCaseStates, setFilterCaseStates] = useState([]);
  const [filterCustomers, setFilterCustomers] = useState([]);
  const [filterFinancialStatus, setFilterFinancialStatus] = useState([]);
  const [filterKey, setFilterKey] = useState(0);

  const financialStatusOptions = [
    { id: "UNLINKED", text: t("BillingContent.FinancialStatusUnlinked") },
    { id: "PENDING", text: t("BillingContent.FinancialStatusPending") },
    { id: "CONCLUDED", text: t("BillingContent.FinancialStatusConcluded") },
  ];

  const suppliers = useSelector(selectSuppliers) || [];
  const serviceTypes = useSelector(selectSupplierServiceTypes) || [];
  const caseStates = useSelector(selectCaseStates) || [];
  const customers = useSelector(selectAllCustomers) || [];

  useEffect(() => {
    dispatch(getSuppliers());
    dispatch(getSupplierServiceTypes());
    dispatch(getCaseStates());
    dispatch(getAllCustomers());
  }, [dispatch]);

  const showClearFilters = () => {
    return (
      filterText ||
      filterStartDate ||
      filterEndDate ||
      (filterSuppliers && filterSuppliers.length > 0) ||
      (filterServiceTypes && filterServiceTypes.length > 0) ||
      (filterCaseStates && filterCaseStates.length > 0) ||
      (filterCustomers && filterCustomers.length > 0) ||
      (filterFinancialStatus && filterFinancialStatus.length > 0)
    );
  };

  const buildFilterQuery = (size, pageIdx, isTicketTab = activeTab === 0) => {
    let params = `?pageNum=${size}&pageIndex=${pageIdx}`;
    if (filterText) {
      params += `&SearchString=${encodeURIComponent(filterText)}`;
    }
    if (filterStartDate) {
      params += `&CreationDate=${filterStartDate}`;
    }
    if (filterEndDate) {
      params += `&CreationDateUntil=${filterEndDate}`;
    }
    if (filterSuppliers && filterSuppliers.length > 0) {
      params += `&Suppliers=${filterSuppliers.join(",")}`;
    }
    if (filterServiceTypes && filterServiceTypes.length > 0) {
      params += `&SupplierServiceTypes=${filterServiceTypes.join(",")}`;
    }
    if (filterCaseStates && filterCaseStates.length > 0) {
      params += `&CaseStates=${filterCaseStates.join(",")}`;
    }
    if (filterCustomers && filterCustomers.length > 0) {
      params += `&CustomerCode=${filterCustomers.join(",")}`;
    }
    if (isTicketTab && filterFinancialStatus && filterFinancialStatus.length > 0) {
      params += `&FinancialStatus=${filterFinancialStatus.join(",")}`;
    }
    return params;
  };

  const fetchTickets = (pageIdx = ticketPage.pageIndex, size = ticketPage.pageSize) => {
    setLoadingTickets("loading");
    const queryStr = buildFilterQuery(size, pageIdx, true);
    baseRequest(
      { query: queryStr },
      dispatch,
      QUERY_GET_BILLING_TICKETS,
      "GET"
    )
      .then((data) => {
        setTickets(data.elements || []);
        setTicketPage((prev) => ({
          ...prev,
          pageIndex: pageIdx,
          pageSize: size,
          totalRecords: data.totalRecords,
          totalPages: data.totalPages,
        }));
        setLoadingTickets("succeeded");
      })
      .catch(() => {
        setLoadingTickets("failed");
        dispatch(
          openModal({
            type: "warning",
            message: "Falha ao carregar lista de faturamentos conciliados.",
          })
        );
      });
  };

  const fetchDrafts = (pageIdx = draftPage.pageIndex, size = draftPage.pageSize) => {
    setLoadingDrafts("loading");
    const queryStr = buildFilterQuery(size, pageIdx, false);
    baseRequest(
      { query: queryStr },
      dispatch,
      QUERY_GET_BILLING_DRAFTS,
      "GET"
    )
      .then((data) => {
        setDrafts(data.elements || []);
        setDraftPage((prev) => ({
          ...prev,
          pageIndex: pageIdx,
          pageSize: size,
          totalRecords: data.totalRecords,
          totalPages: data.totalPages,
        }));
        setLoadingDrafts("succeeded");
      })
      .catch(() => {
        setLoadingDrafts("failed");
        dispatch(
          openModal({
            type: "warning",
            message: "Falha ao carregar lista de rascunhos de faturamento.",
          })
        );
      });
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchTickets(1, ticketPage.pageSize);
    } else {
      fetchDrafts(1, draftPage.pageSize);
    }
  }, [activeTab]);

  const handleApplyFilters = () => {
    if (activeTab === 0) {
      fetchTickets(1, ticketPage.pageSize);
    } else {
      fetchDrafts(1, draftPage.pageSize);
    }
  };

  const handleClearFilters = () => {
    setFilterText("");
    setFilterSuppliers([]);
    setFilterServiceTypes([]);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterCaseStates([]);
    setFilterCustomers([]);
    setFilterFinancialStatus([]);
    setFilterKey((prev) => prev + 1);

    setTimeout(() => {
      if (activeTab === 0) {
        setLoadingTickets("loading");
        baseRequest({ query: `?pageNum=${ticketPage.pageSize}&pageIndex=1` }, dispatch, QUERY_GET_BILLING_TICKETS, "GET")
          .then((data) => {
            setTickets(data.elements || []);
            setTicketPage((p) => ({ ...p, pageIndex: 1, totalRecords: data.totalRecords, totalPages: data.totalPages }));
            setLoadingTickets("succeeded");
          });
      } else {
        setLoadingDrafts("loading");
        baseRequest({ query: `?pageNum=${draftPage.pageSize}&pageIndex=1` }, dispatch, QUERY_GET_BILLING_DRAFTS, "GET")
          .then((data) => {
            setDrafts(data.elements || []);
            setDraftPage((p) => ({ ...p, pageIndex: 1, totalRecords: data.totalRecords, totalPages: data.totalPages }));
            setLoadingDrafts("succeeded");
          });
      }
    }, 50);
  };

  const handleImportFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const isValid = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
      if (!isValid) {
        dispatch(
          openModal({
            type: "warning",
            message: t("BillingContent.InvalidFileFormat"),
          })
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      setUploading(true);
      const submitData = new FormData();
      submitData.append("file", file);

      baseRequest(
        { body: submitData, excludeContentType: true },
        dispatch,
        QUERY_POST_BILLING_IMPORT,
        "POST"
      )
        .then((data) => {
          setUploading(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }

          const message = t("BillingContent.ImportSuccessSummary", {
            totalRows: data.totalRows,
            successfullyImported: data.successfullyImported,
            savedAsDraft: data.savedAsDraft,
          });
          dispatch(openModal({ type: "success", message }));

          if (activeTab === 0) {
            fetchTickets(1, ticketPage.pageSize);
          } else {
            fetchDrafts(1, draftPage.pageSize);
          }
        })
        .catch((err) => {
          setUploading(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          dispatch(
            openModal({
              type: "warning",
              message: err?.message || t("BillingContent.ImportError"),
            })
          );
        });
    }
  };

  const handleExportExcel = async () => {
    const tabStr = activeTab === 0 ? "tickets" : "drafts";
    const bodyData = {
      SearchString: filterText || null,
      Suppliers: filterSuppliers && filterSuppliers.length > 0 ? filterSuppliers.join(",") : null,
      SupplierServiceTypes: filterServiceTypes && filterServiceTypes.length > 0 ? filterServiceTypes.join(",") : null,
      CreationDate: filterStartDate ? filterStartDate : null,
      CreationDateUntil: filterEndDate ? filterEndDate : null,
      CaseStates: filterCaseStates && filterCaseStates.length > 0 ? filterCaseStates.join(",") : null,
      FinancialStatus: filterFinancialStatus && filterFinancialStatus.length > 0 ? filterFinancialStatus.join(",") : null,
    };

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Sao_Paulo";
      const queryParams = `?tab=${tabStr}&timeZone=${encodeURIComponent(timezone)}`;
      const data = await baseRequest(
        { body: bodyData, query: queryParams },
        dispatch,
        QUERY_POST_BILLING_EXPORT,
        "POST"
      );

      if (data) {
        const dateStr = "_" + dayjs().format("YYYYMMDD") + "_" + dayjs().format("HHmmss");
        const filename = (activeTab === 0 ? "Faturamentos_Conciliados" : "Rascunhos_Divergencia") + dateStr + ".xlsx";
        saveAs(new Blob([_base64ToArrayBuffer(data)], { type: "application/octet-stream" }), filename);
      } else {
        dispatch(
          openModal({
            type: "warning",
            message: "Nenhum dado encontrado para exportar.",
          })
        );
      }
    } catch (err) {
      dispatch(
        openModal({
          type: "warning",
          message: err?.message || "Ocorreu um erro ao exportar os dados.",
        })
      );
    }
  };

  const handleOpenLinkModal = (ticket, e) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedChildTicket(ticket);
    setLinkModalOpen(true);
  };

  const handleUnlinkParent = (childTicket, e) => {
    if (e) {
      e.stopPropagation();
    }
    if (window.confirm(t("BillingContent.UnlinkParentConfirmation"))) {
      baseRequest(
        "POST",
        `${QUERY_POST_BILLING_UNLINK_PARENT}/${childTicket.caseId}/unlink-parent`
      )
        .then(() => {
          dispatch(
            openModal({
              type: "success",
              message: t("BillingContent.SuccessUnlinkParent"),
            })
          );
          fetchTickets();
        })
        .catch(() => {
          dispatch(
            openModal({
              type: "error",
              message: t("UnexpectedError") || "Erro ao desvincular chamado.",
            })
          );
        });
    }
  };

  const renderChildTicketsDetail = (detailProps) => {
    const parentTicket = detailProps.data?.data || detailProps.data || {};
    const childTickets = parentTicket.childTickets || [];

    if (!childTickets.length) {
      return null;
    }

    return (
      <ChildTicketsSubTable
        childTickets={childTickets}
        renderFinancialStatus={renderFinancialStatus}
        renderCaseStateColumn={renderCaseStateColumn}
      />
    );
  };

  const handleCellPrepared = (e) => {
    if (e.rowType === "data" && e.column?.command === "expand") {
      if (!e.data?.childTickets || e.data?.childTickets?.length === 0) {
        e.cellElement.innerHTML = "";
        e.cellElement.className = "dx-command-expand";
      }
    }
    if (e.rowType === "detail") {
      e.cellElement.style.padding = "0px";
      e.cellElement.style.margin = "0px";
      e.cellElement.style.border = "none";
    }
  };

  const handleRowPrepared = (e) => {
    if (e.rowType === "data") {
      if (e.data?.childTickets && e.data.childTickets.length > 0) {
        e.rowElement.style.borderLeft = "4px solid #007BC0";
      } else {
        e.rowElement.style.borderLeft = "4px solid transparent";
      }
    }
    if (e.rowType === "detail") {
      e.rowElement.style.borderLeft = "none";
      e.rowElement.style.padding = "0px";
      e.rowElement.style.backgroundColor = "transparent";
    }
  };

  const handleRowExpanding = (e) => {
    const rowIndex = e.component?.getRowIndexByKey ? e.component.getRowIndexByKey(e.key) : -1;
    const rows = e.component?.getVisibleRows ? e.component.getVisibleRows() : [];
    const targetRow = rowIndex >= 0 && rowIndex < rows.length ? rows[rowIndex] : rows.find((r) => r.key === e.key);
    const rowData = targetRow?.data;
    if (rowData && (!rowData.childTickets || rowData.childTickets.length === 0)) {
      e.cancel = true;
    }
  };

  const renderFinancialStatus = (value) => {
    const statusVal = value ? value.toUpperCase() : "";

    let iconSrc = alertWarningIcon;
    let textKey = "BillingContent.FinancialStatusPending";
    let bgColor = "#FFDF95";

    if (statusVal === "APPROVED" || statusVal === "CONCLUDED" || statusVal === "COMPLETED" || statusVal === "CONCLUÍDO" || statusVal === "CONCLUIDO") {
      iconSrc = checkmarkIcon;
      textKey = "BillingContent.FinancialStatusConcluded";
      bgColor = "#B8EFC9";
    } else if (statusVal === "UNLINKED" || statusVal === "SEM_VINCULO" || statusVal === "SEM VÍNCULO" || statusVal === "SEM VINCULO") {
      iconSrc = alertInfoIcon;
      textKey = "BillingContent.FinancialStatusUnlinked";
      bgColor = "#E0E2E5";
    }

    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.375rem",
          backgroundColor: bgColor,
          color: "#000000",
          borderRadius: "16px",
          padding: "0.25rem 0.75rem",
          fontSize: "0.8125rem",
          fontWeight: "normal",
          whiteSpace: "nowrap",
        }}
      >
        <img src={iconSrc} alt="" style={{ width: "1rem", height: "1rem" }} />
        <span>{t(textKey)}</span>
      </div>
    );
  };

  const ticketColumns = [
    <Column key="t-case-number" dataField="caseId" caption={t("CaseId")} alignment="right" />,
    <Column key="t-customer" width="10%" dataField="customerName" caption={t("Customer")} />,
    <Column key="t-mspid" width="6%" dataField="mspid" caption={t("BillingContent.FilterMspid")} />,
    <Column key="t-supplier" dataField="supplierName" caption={t("Supplier")} />,
    <Column key="t-plate" width="6%" dataField="vehiclePlate" caption={t("Plate")} />,
    <Column key="t-protocol" width="6%" dataField="supplierProtocol" caption={t("Protocol")} />,
    <Column key="t-financial-status" width="10%" dataField="financialStatus" caption={t("BillingContent.FinancialStatus")} cellRender={(data) => renderFinancialStatus(data.value)} />,
    <Column key="t-payment-orders-count" width="6%" dataField="paymentOrdersCount" caption={t("BillingContent.PaymentOrdersCount")} alignment="right" cellRender={(row) => row.data?.financialStatus === "UNLINKED" ? "-" : `${row.value || 0} OP${row.value === 1 ? "" : "s"}`} />,
    <Column key="t-total-value" width="7%" dataField="totalValue" caption={t("BillingContent.TotalValue")} alignment="right" cellRender={(row) => row.value != null ? `R$ ${Number(row.value).toFixed(2)}` : "-"} />,
    <Column key="t-approved-value" width="7%" dataField="approvedTotalValue" caption={t("BillingContent.ApprovedTotalValue")} alignment="right" cellRender={(row) => row.value != null ? `R$ ${Number(row.value).toFixed(2)}` : "-"} />,
    <Column key="t-start-date" width="7%" dataField="startDate" caption={t("BillingContent.FilterCaseStartDate")} dataType="date" format="dd/MM/yyyy" />,
    <Column key="t-end-date" width="7%" dataField="endDate" caption={t("BillingContent.FilterCaseEndDate")} dataType="date" format="dd/MM/yyyy" />,
    <Column key="t-agent" dataField="agent" caption={t("BillingContent.FilterAgent")} />,
    <Column key="t-state" dataField="caseState" caption={t("State")} cellRender={(data) => renderCaseStateColumn(data?.data?.caseStateId, data?.value)} />,
  ];

  const draftColumns = [
    <Column key="d-1" width="5%" dataField="id" caption={t("BillingContent.ColId")} alignment="right" />,
    <Column key="d-2" width="10%" dataField="rawOrderProtocol" caption={t("BillingContent.ColProtocol")} />,
    <Column key="d-mspid" width="7%" dataField="rawMspId" caption={t("BillingContent.FilterMspid")} />,
    <Column key="d-3" dataField="rawSupplierName" caption={t("Supplier")} />,
    <Column key="d-4" width="6%" dataField="rawPlate" caption={t("BillingContent.ColPlate")} />,
    <Column key="d-5" dataField="rawVehicleModel" caption={t("BillingContent.ColModel")} />,
    <Column key="d-6" width="8%" dataField="rawTotalValue" caption={t("BillingContent.ColGrossValue")} alignment="right" cellRender={(row) => row.value != null ? `R$ ${Number(row.value).toFixed(2)}` : ""} />,
    <Column key="d-7" dataField="errorDetails" caption={t("BillingContent.ColDivergenceDescription")} />,
  ];

  return (
    <div style={{ margin: "1rem" }}>
      <form
        key={filterKey}
        onSubmit={(e) => {
          e.preventDefault();
          handleApplyFilters();
        }}
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "0.75rem",
          marginBottom: "1.5rem",
        }}
      >
        <div style={{ minWidth: "200px", maxWidth: "300px", flex: "1 1 240px" }}>
          <TextBox
            label={`${t("Search")}...`}
            value={filterText}
            mode="text"
            onEnterKey={handleApplyFilters}
            onValueChange={(val) => setFilterText(val)}
            valueChangeEvent="keyup"
          >
            <TextBoxButton
              name="search"
              location="after"
              options={{
                icon: searchIcon,
                stylingMode: "text",
                onClick: handleApplyFilters,
              }}
            />
          </TextBox>
        </div>

        <div style={{ minWidth: "200px", maxWidth: "300px", flex: "1 1 240px" }}>
          <DateBox
            label={t("BillingContent.FilterStartDate")}
            labelMode="floating"
            placeholder={t("BillingContent.FilterStartDate")}
            useMaskBehavior={true}
            dateSerializationFormat="yyyy-MM-dd"
            displayFormat="dd/MM/yyyy"
            value={filterStartDate}
            onValueChanged={(e) => setFilterStartDate(e.value)}
            type="date"
            acceptCustomValue={false}
            pickerType="calendar"
            buttons={["dropDown"]}
          />
        </div>

        <div style={{ minWidth: "200px", maxWidth: "300px", flex: "1 1 240px" }}>
          <DateBox
            label={t("BillingContent.FilterEndDate")}
            labelMode="floating"
            placeholder={t("BillingContent.FilterEndDate")}
            useMaskBehavior={true}
            dateSerializationFormat="yyyy-MM-dd"
            displayFormat="dd/MM/yyyy"
            value={filterEndDate}
            onValueChanged={(e) => setFilterEndDate(e.value)}
            type="date"
            acceptCustomValue={false}
            pickerType="calendar"
            buttons={["dropDown"]}
          />
        </div>

        <div style={{ minWidth: "200px", maxWidth: "300px", flex: "1 1 240px" }}>
          <TagBox
            id="custom-suppliers"
            value={filterSuppliers}
            className="tagStates"
            label={t("BillingContent.FilterSupplier")}
            placeholder={t("BillingContent.FilterSupplier")}
            labelMode="floating"
            selectAllText={t("SelectAll")}
            dataSource={suppliers}
            showSelectionControls={true}
            multiline={false}
            applyValueMode="instantly"
            displayExpr="description"
            valueExpr="supplierId"
            onValueChanged={(e) => setFilterSuppliers(e.value || [])}
            items={suppliers}
            showClearButton={true}
          />
        </div>

        <div style={{ minWidth: "200px", maxWidth: "300px", flex: "1 1 240px" }}>
          <TagBox
            id="custom-supplier-service-types"
            value={filterServiceTypes}
            className="tagStates"
            label={t("BillingContent.FilterServiceType")}
            placeholder={t("BillingContent.FilterServiceType")}
            labelMode="floating"
            selectAllText={t("SelectAll")}
            dataSource={serviceTypes}
            showSelectionControls={true}
            multiline={false}
            applyValueMode="instantly"
            displayExpr="description"
            valueExpr="supplierServiceTypeId"
            onValueChanged={(e) => setFilterServiceTypes(e.value || [])}
            items={serviceTypes}
            showClearButton={true}
          />
        </div>

        <div style={{ minWidth: "200px", maxWidth: "300px", flex: "1 1 240px" }}>
          <TagBox
            id="custom-case-states"
            value={filterCaseStates}
            className="tagStates"
            label={t("BillingContent.FilterState")}
            placeholder={t("BillingContent.FilterState")}
            labelMode="floating"
            selectAllText={t("SelectAll")}
            dataSource={caseStates}
            showSelectionControls={true}
            multiline={false}
            applyValueMode="instantly"
            displayExpr="description"
            valueExpr="stateId"
            onValueChanged={(e) => setFilterCaseStates(e.value || [])}
            items={caseStates}
            showClearButton={true}
          />
        </div>

        <div style={{ minWidth: "200px", maxWidth: "300px", flex: "1 1 240px" }}>
          <TagBox
            id="customer-filter"
            value={filterCustomers}
            className="tagStates"
            label={t("BillingContent.FilterClient")}
            placeholder={t("BillingContent.FilterClient")}
            labelMode="floating"
            selectAllText={t("SelectAll")}
            dataSource={customers}
            showSelectionControls={true}
            multiline={false}
            applyValueMode="instantly"
            displayExpr="customerName"
            valueExpr="customerCode"
            onValueChanged={(e) => setFilterCustomers(e.value || [])}
            items={customers}
            showClearButton={true}
          />
        </div>

        {activeTab === 0 && (
          <div style={{ minWidth: "200px", maxWidth: "300px", flex: "1 1 240px" }}>
            <TagBox
              id="financial-status-filter"
              value={filterFinancialStatus}
              className="tagStates"
              label={t("BillingContent.FinancialStatus")}
              placeholder={t("BillingContent.FinancialStatus")}
              labelMode="floating"
              selectAllText={t("SelectAll")}
              dataSource={financialStatusOptions}
              showSelectionControls={true}
              multiline={false}
              applyValueMode="instantly"
              displayExpr="text"
              valueExpr="id"
              onValueChanged={(e) => setFilterFinancialStatus(e.value || [])}
              items={financialStatusOptions}
              showClearButton={true}
            />
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleApplyFilters}
          >
            {t("BillingContent.FilterButtonApply")}
          </Button>
          {showClearFilters() && (
            <div style={{ cursor: "pointer" }}>
              <DeleteOutlinedIcon
                style={{ verticalAlign: "middle", color: "#015BA8" }}
                onClick={handleClearFilters}
              />
            </div>
          )}
        </div>

        <div style={{ flexGrow: 1 }} />

        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <Button
            component="label"
            variant="outlined"
            color="primary"
            disabled={uploading}
            startIcon={
              <img
                style={{ height: "1.25rem", width: "1.25rem" }}
                src={importIcon}
                alt=""
              />
            }
          >
            {uploading ? "..." : t("BillingContent.ImportButton")}
            <input
              ref={fileInputRef}
              accept=".xls,.xlsx"
              onChange={handleImportFile}
              style={{ display: "none" }}
              type="file"
              disabled={uploading}
            />
          </Button>

          <Button
            startIcon={
              <img
                style={{
                  height: "1.25rem",
                  width: "1.25rem",
                  filter: "brightness(0) invert(1)",
                }}
                src={exportIcon}
                alt=""
              />
            }
            variant="contained"
            color="primary"
            onClick={handleExportExcel}
          >
            {t("Export")}
          </Button>
        </div>
      </form>

      <div>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab disableRipple label={t("BillingContent.TabTickets")} />
          <Tab disableRipple label={t("BillingContent.TabDrafts")} />
        </Tabs>
      </div>

      <Divider style={{ marginBottom: "1.5rem" }} />

      {activeTab === 0 ? (
        <div key="billing-tickets-tab">
          <Table
            key="billing-tickets-table"
            datasource={tickets}
            loading={loadingTickets}
            columnasTabla={ticketColumns}
            master={true}
            masterDetail={renderChildTicketsDetail}
            onCellPrepared={handleCellPrepared}
            onRowPrepared={handleRowPrepared}
            onRowExpanding={handleRowExpanding}
            handleRowClick={(e) => {
              if (e.data && e.data.caseId) {
                window.location.href = `/?cod_cliente=${e.data.caseId}`;
              }
            }}
          />
          <CustomPagination
            pageSize={ticketPage.pageSize}
            pageIndex={ticketPage.pageIndex}
            handlePageIndexChange={(e, val) => {
              setTicketPage((p) => ({ ...p, pageIndex: val }));
              fetchTickets(val, ticketPage.pageSize);
            }}
            handlePageSizeChange={(val) => {
              setTicketPage((p) => ({ ...p, pageSize: val, pageIndex: 1 }));
              fetchTickets(1, val);
            }}
            loading={loadingTickets}
            totalPages={ticketPage.totalPages}
            refreshPage={() => fetchTickets(ticketPage.pageIndex, ticketPage.pageSize)}
            count={ticketPage.totalRecords}
          />
        </div>
      ) : (
        <div key="billing-drafts-tab">
          <Table
            key="billing-drafts-table"
            datasource={drafts}
            loading={loadingDrafts}
            columnasTabla={draftColumns}
          />
          <CustomPagination
            pageSize={draftPage.pageSize}
            pageIndex={draftPage.pageIndex}
            handlePageIndexChange={(e, val) => {
              setDraftPage((p) => ({ ...p, pageIndex: val }));
              fetchDrafts(val, draftPage.pageSize);
            }}
            handlePageSizeChange={(val) => {
              setDraftPage((p) => ({ ...p, pageSize: val, pageIndex: 1 }));
              fetchDrafts(1, val);
            }}
            loading={loadingDrafts}
            totalPages={draftPage.totalPages}
            refreshPage={() => fetchDrafts(draftPage.pageIndex, draftPage.pageSize)}
            count={draftPage.totalRecords}
          />
        </div>
      )}

      <LinkParentTicketModal
        open={linkModalOpen}
        childTicket={selectedChildTicket}
        onClose={() => {
          setLinkModalOpen(false);
          setSelectedChildTicket(null);
        }}
        onLinked={() => fetchTickets()}
      />
    </div>
  );
}
